// src/components/Login.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Si tu proyecto NO tiene ../constants/ui o no exporta COLORS, esto evitará crash.
let COLORS_SAFE = {};
try {
  // eslint-disable-next-line global-require
  COLORS_SAFE = require("../constants/ui").COLORS || {};
} catch {
  COLORS_SAFE = {};
}

const GlobalLoginCSS = () => (
  <style>{`
    :root{
      --text: #0f172a;
      --muted: rgba(15,23,42,.72);
      --accent: #06b6d4;
      --accent2:#2563eb;
      --ring: rgba(6,182,212,.35);
      --border: rgba(255,255,255,.22);
      --shadow: 0 18px 50px rgba(2,6,23,.18);
    }

    html, body, #root{ height:100%; }
    body{
      margin:0;
      color:var(--text);
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    }

    .vh{
      min-height: 100vh;
      min-height: 100svh;
      min-height: 100dvh;
    }

    .auth-root{
      position: relative;
      overflow: hidden;
    }

    .bg-slider{
      position: absolute;
      inset: 0;
      z-index: 0;
    }
    .bg-slider .slick-slider,
    .bg-slider .slick-list,
    .bg-slider .slick-track,
    .bg-slider .slick-slide > div{
      height: 100%;
    }

    .bg-slide{
      position: relative;
      width: 100%;
      height: 100%;
    }

    .bg-img{
      width:100%;
      height:100%;
      object-fit:cover;
      transform: scale(1.04);
      filter: saturate(1.05) contrast(1.02);
    }

    .bg-overlay{
      position:absolute;
      inset:0;
      background:
        radial-gradient(900px 600px at 12% 10%, rgba(37,99,235,.25), transparent 55%),
        radial-gradient(900px 600px at 88% 12%, rgba(6,182,212,.28), transparent 50%),
        radial-gradient(900px 600px at 30% 90%, rgba(99,102,241,.18), transparent 55%),
        linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.65));
    }

    .auth-content{
      position: relative;
      z-index: 2;
      height: 100%;
      display: grid;
      align-items: center;
      padding: clamp(14px, 3.5vw, 36px);
    }

    .wrap{
      width: min(980px, 92vw);
      margin: 0 auto;
      display: grid;
      grid-template-columns: 1.05fr .95fr;
      gap: clamp(12px, 2.6vw, 22px);
      align-items: start;
    }

    @media (max-width: 980px){
      .wrap{ grid-template-columns: 1fr; }
    }

    .card{
      background: rgba(255,255,255,.14);
      border: 1px solid var(--border);
      border-radius: 22px;
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      padding: clamp(14px, 3.2vw, 28px);
      color: #fff;
    }

    .brand{
      display:flex;
      align-items:center;
      gap: 12px;
      margin-bottom: 10px;
    }

    .logo{
      width: 44px;
      height: 44px;
      border-radius: 14px;
      background: rgba(255,255,255,.18);
      border: 1px solid rgba(255,255,255,.22);
      display:flex;
      align-items:center;
      justify-content:center;
      overflow:hidden;
      flex: 0 0 auto;
    }
    .logo img{ width:100%; height:100%; object-fit: cover; }

    .title{
      margin:0;
      font-size: clamp(22px, 3.6vw, 36px);
      font-weight: 950;
      letter-spacing: .2px;
      line-height: 1.08;
    }
    .title span{
      background: linear-gradient(90deg, var(--accent2), var(--accent));
      -webkit-background-clip: text;
      background-clip:text;
      color: transparent;
    }

    .lead{
      margin: 10px 0 0;
      font-size: 14px;
      line-height: 1.75;
      color: rgba(255,255,255,.88);
      max-width: 72ch;
    }

    .input{
      width:100%;
      background: rgba(255,255,255,.14);
      border: 1px solid rgba(255,255,255,.22);
      color: rgba(255,255,255,.92);
      padding: 12px 12px;
      border-radius: 14px;
      font-size: 15px;
      outline:none;
      box-shadow: 0 10px 25px rgba(2,6,23,.14);
      transition: box-shadow .12s ease, border-color .12s ease, transform .12s ease;
    }
    .input::placeholder{ color: rgba(255,255,255,.72); }
    .input:focus{
      border-color: rgba(6,182,212,.65);
      box-shadow: 0 0 0 4px var(--ring), 0 14px 30px rgba(2,6,23,.22);
      transform: translateY(-1px);
    }

    .btn{
      cursor:pointer;
      border: 0;
      border-radius: 14px;
      padding: 12px 14px;
      font-weight: 950;
      transition: transform .12s ease, filter .2s ease, opacity .12s ease;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      gap: 10px;
      user-select: none;
      white-space: nowrap;
      width: 100%;
    }
    .btn:active{ transform: translateY(1px); }
    .btn:disabled{ opacity:.65; cursor:not-allowed; }

    .btn-primary{
      background: linear-gradient(180deg, rgba(6,182,212,1), rgba(37,99,235,1));
      color:#062028;
      box-shadow: 0 12px 30px rgba(6,182,212,.18);
    }
    .btn-ghost{
      background: rgba(255,255,255,.14);
      color:#fff;
      border: 1px solid rgba(255,255,255,.22);
    }
    .btn:hover{ filter: brightness(1.05); }

    .links{
      display:flex;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: 12px;
      font-size: 13px;
      color: rgba(255,255,255,.85);
    }
    .links a{
      color: rgba(255,255,255,.92);
      font-weight: 950;
      text-decoration: none;
      border-bottom: 1px dashed rgba(255,255,255,.55);
      padding-bottom: 2px;
    }
    .links a:hover{ border-bottom-color: rgba(6,182,212,.95); }

    .msg{
      margin-top: 10px;
      font-weight: 900;
      border: 1px solid rgba(255,255,255,.22);
      background: rgba(255,255,255,.12);
      color: rgba(255,255,255,.92);
      padding: 10px 12px;
      border-radius: 14px;
      box-shadow: 0 10px 25px rgba(2,6,23,.12);
    }

    .side{
      display:grid;
      gap: 12px;
    }
    .quickCard{
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.20);
      border-radius: 18px;
      padding: 14px 14px;
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      color:#fff;
    }
    .quickTitle{ font-weight: 950; margin:0; font-size: 15px; }
    .quickText{
      margin: 6px 0 0;
      font-size: 13px;
      color: rgba(255,255,255,.84);
      line-height: 1.6;
    }

    .bg-slider .slick-dots{
      bottom: 16px;
      z-index: 6;
    }
    .bg-slider .slick-dots li button:before{
      color: rgba(255,255,255,.62);
      opacity: 1;
      font-size: 10px;
    }
    .bg-slider .slick-dots li.slick-active button:before{
      color: rgba(6,182,212,1);
    }

    .bg-slider .slick-prev, .bg-slider .slick-next{
      z-index: 7;
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: rgba(255,255,255,.16);
      border: 1px solid rgba(255,255,255,.22);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .bg-slider .slick-prev{ left: 16px; }
    .bg-slider .slick-next{ right: 16px; }
    .bg-slider .slick-prev:before, .bg-slider .slick-next:before{
      color: rgba(255,255,255,.92);
      opacity: 1;
      font-size: 20px;
    }

    @media (max-width: 560px){
      .bg-slider .slick-prev,
      .bg-slider .slick-next{ display:none !important; }
      .bg-slider .slick-dots{ bottom: 10px; }
    }

    @media (max-height: 680px){
      .auth-content{
        align-items: start;
        padding-top: 14px;
        padding-bottom: 38px;
      }
    }
  `}</style>
);

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [msg, setMsg] = useState("");

  const slides = useMemo(
    () => [
      {
        src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?fm=jpg&q=60&w=2400&ixlib=rb-4.0.3",
        alt: "Publicidad en vehículos",
      },
      {
        src: "https://kudeko.com/cdn/shop/products/cuadro-nueva-york-ciudad-sin-marco-fotografia-blanco-negro_800x.jpg?v=1666951353",
        alt: "Ciudad nocturna",
      },
      {
        src: "https://as2.ftcdn.net/v2/jpg/02/70/19/17/1000_F_270191793_5Nk3dvcKjuUgCuNJZ4tzL5tTNsjQlkqh.jpg",
        alt: "Tráfico urbano al atardecer",
      },
    ],
    []
  );

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 700,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4200,
      arrows: true,
      pauseOnHover: false,
      pauseOnFocus: false,
      swipe: true,
      touchMove: true,
      adaptiveHeight: false,
    }),
    []
  );

  const accent = COLORS_SAFE?.accent || "#06b6d4";

  const submit = (e) => {
    e.preventDefault();
    setMsg("");

    const u = form.username.trim().toLowerCase();
    if (!u) return setMsg("Escribe tu usuario.");

    // Demo: “loguear” guardando en localStorage
    localStorage.setItem("username", u);

    // Si no existe createdAtMs, lo creamos (para Mi Pago)
    const createdAtMs = Number(localStorage.getItem("createdAtMs") || 0);
    if (!createdAtMs) localStorage.setItem("createdAtMs", String(Date.now()));

    navigate("/socio");
  };

  return (
    <>
      <GlobalLoginCSS />

      <div className="auth-root vh">
        {/* Fondo carrusel */}
        <div className="bg-slider" aria-hidden="true">
          <Slider {...settings}>
            {slides.map((s, i) => (
              <div key={i}>
                <div className="bg-slide">
                  <img className="bg-img" src={s.src} alt={s.alt || "Slide"} />
                  <div className="bg-overlay" />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Contenido */}
        <div className="auth-content">
          <div className="wrap">
            {/* Columna izquierda: Login */}
            <div className="card">
              <div className="brand">
                <div className="logo">
                  <img src="/screen-do-logo.png" alt="Screen DO" />
                </div>
                <div>
                  <div style={{ fontWeight: 950, color: "rgba(255,255,255,.92)" }}>Screen DO</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                    Acceso de Socios
                  </div>
                </div>
              </div>

              <h1 className="title">
                Inicia sesión <span>rápido</span>.
              </h1>

              <p className="lead">
                Accede a tu panel para ver tus productos, sesiones y el estado de tu pago mensual.
              </p>

              <form onSubmit={submit} style={{ marginTop: 14, display: "grid", gap: 10 }}>
                <input
                  className="input"
                  placeholder="Usuario"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="username"
                />
                <input
                  className="input"
                  placeholder="Contraseña"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="current-password"
                />

                <button className="btn btn-primary" type="submit">
                  🔐 Entrar
                </button>

                {msg && <div className="msg">{msg}</div>}

                <div className="links">
                  <Link to="/signup">Crear cuenta</Link>
                  <span style={{ opacity: 0.55 }}>|</span>
                  <Link to="/" style={{ color: accent }}>
                    Volver
                  </Link>
                </div>
              </form>
            </div>

            {/* Columna derecha: info rápida */}
            <div className="side">
              <div className="quickCard">
                <p className="quickTitle">📌 Mi Pago</p>
                <p className="quickText">
                  Consulta tu historial mensual, estado (Procesando/Pago) y el detalle de comisiones.
                </p>
              </div>

              <div className="quickCard">
                <p className="quickTitle">🛍️ Productos</p>
                <p className="quickText">
                  Revisa tu inventario disponible y el catálogo con tu permiso por puntos.
                </p>
              </div>

              <div className="quickCard">
                <p className="quickTitle">📈 Sesiones</p>
                <p className="quickText">
                  Visualiza tus sesiones diarias y rendimiento en el panel de socios.
                </p>
              </div>

              <div className="quickCard" style={{ opacity: 0.92 }}>
                <p className="quickTitle">🎨 Acento</p>
                <p className="quickText">
                  Tip: tu acento actual es{" "}
                  <b style={{ color: accent }}>{accent}</b>
                </p>
              </div>
            </div>
          </div>

          <div style={{ height: 18 }} />
        </div>
      </div>
    </>
  );
}
